import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone, type Accept } from 'react-dropzone'
import { User } from 'lucide-react'
import StyledSingleImageUploader, {
	StyledSingleImageUploaderWrapper,
} from './SingleImageUploader.style'
import {
	uploadToCloudinary,
	normalizeCloudinaryResult,
	type NormalizedFile,
} from './fileUploadUtils'

type SingleImageUploaderProps = {
	value?: string | null
	onChange: (file: NormalizedFile) => void
	size?: number
	className?: string
	accept?: Accept
	placeholderIcon?: React.ReactNode
	description?: string
	disabled?: boolean
	label?: string
}

const defaultDescription = 'Drag & drop or click to upload'

const SingleImageUploader: React.FC<SingleImageUploaderProps> = ({
	value = null,
	onChange,
	size = 180,
	className = '',
	accept = { 'image/*': [] },
	placeholderIcon = <User className="w-4 h-4" />,
	description = defaultDescription,
	disabled = false,
	label = 'Profile Photo',
}) => {
	const [previewUrl, setPreviewUrl] = useState<string | null>(value)
	const [isUploading, setIsUploading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		setPreviewUrl(value ?? null)
	}, [value])

	const handleUploadComplete = useCallback(
		(file: NormalizedFile) => {
			setIsUploading(false)
			setError(null)
			if (file.originalUrl) {
				setPreviewUrl(file.originalUrl)
			}
			onChange(file)
		},
		[onChange],
	)

	const uploadFromFile = useCallback(
		async (file: File) => {
			setError(null)
			setIsUploading(true)
			try {
				const result = await uploadToCloudinary(file, { resourceType: 'image' })
				handleUploadComplete(normalizeCloudinaryResult(result, 'drag-and-drop'))
			} catch (err) {
				console.error('[SingleImageUploader] Failed to upload file', err)
				setError('Upload failed. Please try again.')
				setIsUploading(false)
			}
		},
		[handleUploadComplete],
	)

	const { getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
		noClick: true,
		noKeyboard: true,
		multiple: false,
		disabled: disabled || isUploading,
		accept,
		onDropAccepted: files => {
			if (!files.length) return
			uploadFromFile(files[0])
		},
		onDropRejected: () => {
			setError('Unsupported file type. Please choose another file.')
		},
	})

	const overlayLabel = useMemo(() => {
		if (disabled) return 'Uploads disabled. Please add a Company Name to enable your photo upload.'
		if (isUploading) return 'Uploading...'
		if (previewUrl) return 'Click to change'
		return 'Click to upload'
	}, [disabled, isUploading, previewUrl])

	const statusLabel = useMemo(() => {
		if (error) return error
		if (isUploading) return 'Uploading...'
		return null
	}, [error, isUploading])

	return (
		<StyledSingleImageUploaderWrapper className={className}>
			<StyledSingleImageUploader
				size={size}
				isDragging={isDragActive}
				isUploading={isUploading}
				hasImage={Boolean(previewUrl)}
				onClick={() => {
					if (!disabled) openFileDialog()
				}}
			>
				<div className="uploader__dropzone" {...getRootProps()}>
					<input {...getInputProps()} />
					{previewUrl ? (
						<img className="uploader__image" src={previewUrl} alt={label} />
					) : (
						<div className="uploader__placeholder">
							{placeholderIcon}
							<span>{label}</span>
							<p>{description}</p>
						</div>
					)}

					<div className="uploader__overlay">{overlayLabel}</div>
				</div>

				{statusLabel && !error && <div className="uploader__status">{statusLabel}</div>}
				{error && <div className="uploader__error">{error}</div>}
			</StyledSingleImageUploader>

			<p className="uploader__tip">Tip: Drag & drop a new photo or click the frame to choose one.</p>
		</StyledSingleImageUploaderWrapper>
	)
}

export default SingleImageUploader
